<template>
  <div class="mc-editable-table">
    <div class="clearfix mt-3">
      <div class="d-flex mc-list-header">
        <h4 class="card-title flex-grow-1">{{ $t('master.product.unitConversion') }}</h4>
        <McButton
          v-if="!isAddingproduct && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 mb-4 ms-auto"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          :disabled="isEditingProduct"
          @click="handleOpenDeleteModal"
        />

        <McButton
          v-if="!isAddingproduct && items.length"
          variant="primary"
          class="mb-4 ms-auto"
          :text="$t('global.addField')"
          icon="icon-fill_plus"
          :disabled="isEditingTable || isEditingProduct"
          @click="handleAdd"
        />
      </div>
    </div>

    <b-card>
      <b-row v-show="!isAddingproduct && selectedIdsLength && !isEditingProduct" align-h="end">
        <b-col>
          <h6 class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a @click="handleUnselectAll" class="color-primary">{{
              $t('global.action.deselect')
            }}</a>
          </h6>
        </b-col>
      </b-row>

      <b-row class="g-0">
        <div
          v-if="(!totalCount || isAddingproduct) && !isEditingTable"
          class="empty-table-container"
        >
          <strong v-if="isAddingproduct">{{ $t('master.product.infoConversion') }}</strong>
          <McButton
            variant="primary"
            class="mt-3"
            :text="$t('global.addField')"
            icon="icon-outline_plus"
            :disabled="isAddingproduct || isEditingProduct"
            @click="handleAdd"
          />
        </div>

        <FormulateForm
          v-else
          v-model="formValues"
          :errors="formErrors"
          name="editable-table-form"
          ref="formRef"
        >
          <div class="table-responsive my-3 border border-1 mc-table-overflow">
            <b-table
              :busy="isBusy"
              :fields="computedFields"
              :items="items"
              fixed
              class="editable-table"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2"></b-spinner>
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    @change="handleSelectAll"
                    v-model="selectedAll"
                    :disabled="disableActions"
                  ></b-form-checkbox>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    @change="(event) => handleSelect(event, data)"
                    :checked="selectedIds[data.item.id]"
                    :disabled="disableActions"
                  ></b-form-checkbox>
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    {{ data.label }}
                  </div>
                  <i
                    v-if="data.field.tooltip"
                    :id="`${data.field.key}-input-tooltip`"
                    class="icon-outline_info float-end"
                  ></i>
                  <b-tooltip
                    v-if="data.field.tooltip"
                    :target="`${data.field.key}-input-tooltip`"
                    triggers="hover"
                    placement="lefttop"
                    class="input-tooltip"
                  >
                    {{ data.field.tooltip }}
                  </b-tooltip>
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="field.isEditable && isEditingRow == index">
                  <FormulateInput
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.inputName || field.key"
                    :type="field.type || 'text'"
                    :value="getInputInitialValue({ field, item })"
                    validation-behavior="live"
                    error-behavior="live"
                    size="sm"
                    :initialObject="
                      field.initialObjectKey
                        ? { id: null, name: item[field.initialObjectKey] }
                        : undefined
                    "
                  />
                </template>

                <!-- Conversion Amount Column -->
                <template v-else-if="field.key === 'conversionAmount'"> 1 </template>

                <!-- Equal Column -->
                <template v-else-if="field.key === 'equal'"><strong> = </strong></template>

                <!-- Base UOM Column -->
                <template v-else-if="field.key === 'base_uom'">
                  {{ computedBaseUom }}
                </template>

                <template v-else>
                  {{ typeof value === 'number' && field.key === 'price' ? value.toLocaleString() : value }}
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <McButton
                    variant="outline"
                    class="__accept-button btn"
                    icon="icon-fill_checkmark"
                    text=""
                    @click="handleAccept(data)"
                  />

                  <McButton
                    variant="outline"
                    class="__cancel-button btn"
                    icon="icon-outline_close"
                    text=""
                    @click="handleEditCancel(data)"
                  />
                </div>

                <McButton
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: disableActions,
                  }"
                  :disabled="isEditingProduct"
                  @click="handleEditStart(data)"
                  icon="icon-outline_edit"
                  :text="$t('global.action.edit')"
                />
              </template>
            </b-table>
          </div>
        </FormulateForm>
      </b-row>
    </b-card>

    <b-modal
      class="mc-modal"
      id="mc-modal-delete"
      ref="mc-modal-delete"
      :title="$t('global.action.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            @click="cancel()"
            variant="secondary"
            class="me-2"
            :text="$t('global.action.cancel')"
          />
          <McButton @click="handleDelete" variant="primary" :text="$t('global.action.delete')" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { multiply } from '@/helpers/calculate'
import McButton from '@/components/McButton'

import * as UomConversionApi from '@/services/rest-api/lib/product/uom-conversion'
import uomConversionFields from '../lib/fields/uomConversionFields'
import uomConversionApiErrorMappings from '../lib/constants/uom-conversion-api-error-mappings'

export default {
  components: {
    McButton,
  },
  props: {
    isAddingproduct: { type: Boolean },
    productId: { type: String },
    uomValues: { type: Object },
    handleDisableEdit: {},
    isEditingProduct: { type: Boolean },
  },
  data() {
    return {
      fields: uomConversionFields,

      items: [],
      ids: [],

      totalCount: null,
      isEditingRow: null,
      isBusy: false,
      isAdding: false,
      isSubmitting: false,
      isEditingTable: false,
      selectedAll: false,
      selectedIdsLength: 0,

      selectedIds: {},
      formValues: {},
      formErrors: {},
      invalidFormValues: {},

      computedBaseUom: '',
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    apiClient() {
      return {
        get: UomConversionApi.get(this.productId),
        show: UomConversionApi.show(this.productId),
        store: UomConversionApi.store(this.productId),
        update: UomConversionApi.update(this.productId),
        deleteSingle: UomConversionApi.deleteSingle(this.productId),
      }
    },
    disableActions() {
      return this.isEditingRow !== null || this.isBusy || this.isEditingProduct
    },
    errorKeys() {
      return Object.keys(this.formErrors)
    },
  },
  watch: {
    formValues() {
      if (this.errorKeys.length === 0) return

      this.errorKeys.forEach((errorKey) => {
        if (this.invalidFormValues[errorKey] !== this.formValues[errorKey]) {
          // delete the fields from the state to remove the error message
          this.$delete(this.formErrors, errorKey)
          this.$delete(this.invalidFormValues, errorKey)
        }
      })

      if (this.errorKeys.length === 0) {
        this.resetErrors()
      }
    },
    'formValues.amount': function (newValue, oldValue) {
      const conversionKeys = ['weight', 'volume', 'price']

      if (newValue === undefined || newValue === null || newValue === '') {
        conversionKeys.forEach((key) => (this.formValues[key] = ''))
        return
      }

      if (newValue === oldValue) {
        return
      }

      const numericAmount = Number(newValue)

      if (!Number.isFinite(numericAmount)) {
        conversionKeys.forEach((key) => (this.formValues[key] = ''))
        return
      }

      conversionKeys.forEach((key) => {
        const baseValue = Number(this.uomValues[key])
        const multiplier = Number.isFinite(baseValue) ? baseValue : 0
        const result = multiply(numericAmount, multiplier)
        this.formValues[key] = result == 0 ? '' : result
      })
    },
  },
  async mounted() {
    if (!this.isAddingproduct) {
      await this.$nextTick()
      await this.getData()
    }
  },
  updated() {
    if (this.computedBaseUom !== this.uomValues.uom) this.computedBaseUom = this.uomValues.uom
  },
  methods: {
    async getData() {
      this.isBusy = true
      const res = await this.apiClient.get()
      if (res.errors) return
      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.totalCount = res.metadata.total_count
      this.computedBaseUom = this.uomValues.uom

      this.isBusy = false
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (item[field.key]) {
        return item[field.key] || false
      }

      return false
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == 'id') return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    async handleAdd() {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      if (this.isEditingProduct) return

      const uomLimit = 5

      if (this.totalCount >= uomLimit) {
        this.$mcToast.warning(`Hanya dapat menyimpan ${uomLimit} satuan konversi`)
        return
      }

      this.isEditingRow = 0
      this.isAdding = true
      this.isEditingTable = true
      this.handleDisableEdit(true)

      this.items = [{ id: 0 }, ...this.items]
      await this.$nextTick()
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      if (this.isEditingProduct) return

      const refetchedData = await this.apiClient.show(data.item.id)
      if (refetchedData.errors) return

      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return refetchedData.data
        return item
      })

      this.isEditingRow = data.index
      this.isEditingTable = true
      this.handleDisableEdit(true)
      await this.$nextTick()
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }

      this.resetErrors()

      this.isEditingRow = null
      this.isEditingTable = false
      this.handleDisableEdit(false)
    },
    async handleAccept(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (this.$refs.formRef.hasErrors || Object.keys(this.errorKeys).length !== 0) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      this.formValues = Object.assign(
        {},
        ...Object.keys(this.formValues).map((key) => {
          let currentField = this.fields.find((item) => item.key == key)

          const value = this.formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0

            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: this.formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )

      let newData = { ...data.item, ...this.formValues }
      const [patchData, hasChanged] = this.filterUnchangedData(newData, this.items[data.index])

      if (!this.isAdding && !hasChanged) {
        this.resetErrors()

        this.isSubmitting = false
        this.isEditingRow = null
        this.isEditingTable = false
        this.handleDisableEdit(true)
        return
      }

      if (this.isAdding) {
        const createdData = await this.apiClient.store(newData)
        if (createdData.errors) {
          this.assignErrors(createdData)
          this.isSubmitting = false
          return
        }

        const refetchedData = await this.apiClient.show(createdData.data.id)
        if (refetchedData.errors) {
          this.isSubmitting = false
          return
        }

        newData = {
          ...newData,
          ...refetchedData.data,
        }
        this.ids.push(refetchedData.data.id)
        this.totalCount += 1
        this.isAdding = false

        this.$mcToast.success('Data berhasil ditambahkan')
      } else {
        const editedData = await this.apiClient.update(data.item.id, patchData)
        if (editedData.errors) {
          this.assignErrors(editedData)
          this.isSubmitting = false
          return
        }
        const refetchedData = await this.apiClient.show(data.item.id)
        if (refetchedData.errors) {
          this.isSubmitting = false
          return
        }
        newData = {
          ...newData,
          ...refetchedData.data,
        }

        this.$mcToast.success('Data berhasil diubah')
      }

      this.items = this.items.map((item) => {
        if (item.id != data.item.id) return item
        return {
          ...newData,
        }
      })

      this.resetErrors()

      this.isSubmitting = false
      this.isEditingRow = null
      this.isEditingTable = false
      this.handleDisableEdit(false)
    },
    handleOpenDeleteModal() {
      if (this.isEditingProduct) return

      this.$refs['mc-modal-delete'].show()
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.deleteSingle(key))
        }
      }
      await Promise.all(promises)
      await this.getData()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
    },
    assignErrors(data) {
      if (data.status === 500) return

      if (data.status === 400) {
        const errorKeys = Object.keys(data.errors)

        errorKeys.forEach((errorKey) => {
          const errorMessage =
            uomConversionApiErrorMappings[errorKey][data.errors[errorKey][0]] ??
            'Terdapat kesalahan format pada input anda'

          this.$set(this.formErrors, errorKey, errorMessage)
          this.$set(this.invalidFormValues, errorKey, this.formValues[errorKey])
        })
      }

      this.$mcToast.error('Gagal simpan. Mohon periksa kembali data Anda.')
    },
    resetErrors() {
      this.formErrors = {}
      this.invalidFormValues = {}
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
